<template>
    <tr class="ix-table-row-secondary ix-kit-detail">
        <td></td>
        <td :colspan="colspan - 1">
            <table class="ix-table ix-table-sm">
                <thead>
                <tr>
                    <th>Référence</th>
                    <th>Désignation</th>
                    <th class="text-right">Qté / kit</th>
                    <th>Unité</th>
                    <th class="text-right">Qté totale</th>
                    <th class="text-right">Stock dispo</th>
                    <th class="text-right" v-if="shows_lead_time">Délai</th>
                    <th class="text-right" v-if="has_eco">Éco / u</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="component in kit.components" :key="component.article_id">
                    <td>
                        <a :href="component.url" target="_blank" class="ix-link" v-if="component.url && component.number">{{ component.number }}</a>
                        <span v-else>{{ component.number || '-' }}</span>
                    </td>
                    <td>
                        <!-- Sans référence, le lien vers la fiche passe sur la désignation. -->
                        <a :href="component.url" target="_blank" class="ix-link" v-if="component.url && !component.number">{{ component.name }}</a>
                        <span v-else>{{ component.name }}</span>
                    </td>
                    <td class="text-right">{{ format(component.quantity) }}</td>
                    <td>{{ component.unit || 'U' }}</td>
                    <td class="text-right">{{ format(total(component)) }}</td>
                    <td class="text-right">
                        <span class="ix-text-secondary" v-if="!component.is_manage">Non suivi</span>
                        <span :class="covers(component) ? 'ix-text-green' : 'ix-text-red'" v-else>{{ format(component.available_stock) }}</span>
                    </td>
                    <!-- Le délai n'a de sens que pour ce qui manque, ou ce qui n'est pas en stock. -->
                    <td class="text-right" v-if="shows_lead_time">
                        <template v-if="component.is_manage && !covers(component) && supplyOf(component).incoming">
                            <a :href="incomingOf(component).url" target="_blank" class="ix-link" :class="{'ix-text-red': incomingOf(component).late}" v-if="incomingOf(component).url"
                               v-b-tooltip="{ title: incomingOf(component).tooltip, trigger: 'hover', placement: 'top', boundary: 'window', customClass: 'ix-line-tooltip' }">{{ incomingOf(component).label }}</a>
                            <span :class="{'ix-text-red': incomingOf(component).late}" v-else
                                  v-b-tooltip="{ title: incomingOf(component).tooltip, trigger: 'hover', placement: 'top', boundary: 'window', customClass: 'ix-line-tooltip' }">{{ incomingOf(component).label }}</span>
                            <div class="ix-text-small" v-if="supplyOf(component).rest"
                                 v-b-tooltip="{ title: restTooltip(supplyOf(component).rest), trigger: 'hover', placement: 'top', boundary: 'window', customClass: 'ix-line-tooltip' }">{{ restLabel(component) }}</div>
                        </template>
                        <template v-else-if="!component.is_manage || !covers(component)">
                            <span v-if="component.lead_time"
                                  v-b-tooltip="{ title: component.lead_time.origin, trigger: 'hover', placement: 'top', boundary: 'window', customClass: 'ix-line-tooltip' }">{{ component.lead_time.days }} j</span>
                            <span class="ix-text-secondary" v-else-if="component.is_manage">Non renseigné</span>
                        </template>
                    </td>
                    <!-- Ce que le composant apporte à la ligne d'éco du kit, par unité de composant. -->
                    <td class="text-right" v-if="has_eco">
                        <span v-for="(eco, i) in component.eco" :key="i" class="d-block" v-b-tooltip.hover :title="eco.label || 'Tarif fixe fournisseur'">
                            <i class="far fa-recycle ix-text-secondary mr-1"></i>{{ currency(eco.unit_amount) }}
                        </span>
                    </td>
                </tr>
                </tbody>
            </table>

            <div class="d-flex align-items-center mt-2 ix-text-small">
                <span>
                    Kits assemblables : {{ kit.assemblable !== null ? kit.assemblable : 'non suivi' }}
                    <span class="ix-text-red" v-if="shortages.length > 0"> · {{ shortages.join(' · ') }}</span>
                    <span v-if="kit.unit_cost !== null"> · Prix de revient {{ currency(kit.unit_cost * lineQuantity) }}</span>
                </span>
                <a :href="kit.url" target="_blank" class="ix-link ml-auto" v-if="kit.url">Fiche de l'article composé</a>
            </div>
        </td>
    </tr>
</template>

<script>
    import { supply, incomingText, restText, restTooltip, todayString } from './lineAvailability';

    /** Le détail d'un kit sous sa ligne, en lecture seule. @see docs/DRI/Phase2/lignes-articles-composes.md § 9.1 */
    export default {
        name: 'kit-detail-row',
        // withLeadTime : le délai ne sert qu'à ce qu'on promet, devis et études ; une commande est déjà promise.
        props: {
            kit: Object,
            quantity: [Number, String],
            colspan: [Number, String],
            demand: Object,
            withLeadTime: { type: Boolean, default: true },
        },
        computed: {
            lineQuantity: function() {
                return parseFloat(this.quantity) || 0;
            },
            shows_lead_time: function() {
                return this.withLeadTime && !!this.kit.shows_lead_time;
            },
            has_eco: function() {
                return this.kit.components.some(component => component.eco && component.eco.length > 0);
            },
            /** Les composants que le stock ne couvre pas, avec l'écart : « COFFR-CMD-P : manque 1 U ». */
            shortages: function() {
                return this.kit.components
                    .filter(component => component.is_manage && !this.covers(component))
                    .map(component => (component.number || component.name) + ' : manque '
                        + this.format(this.need(component) - Math.max(0, component.available_stock || 0))
                        + ' ' + (component.unit || 'U'));
            }
        },
        methods: {
            total: function(component) {
                return component.quantity * this.lineQuantity;
            },
            /** Sur un devis, le besoin du devis entier : un composant peut aussi être vendu seul ou dans un autre kit. */
            need: function(component) {
                var entry = this.demand ? this.demand[component.article_id] : null;

                return entry ? entry.quantity : this.total(component);
            },
            /** Ce qui couvre le manque d'un composant : ses commandes en cours, puis son délai fournisseur. */
            supplyOf: function(component) {
                return supply(this.need(component) - Math.max(0, component.available_stock || 0), component.incoming, component.lead_time, todayString(), component.incoming_url);
            },
            incomingOf: function(component) {
                return incomingText(this.supplyOf(component).incoming, component.unit);
            },
            restTooltip: restTooltip,
            restLabel: function(component) {
                return restText(this.supplyOf(component).rest, component.unit);
            },
            covers: function(component) {
                return (component.available_stock || 0) >= this.need(component) - 0.0001;
            },
            /** Une quantité sans décimales inutiles : « 12 » et non « 12,000 ». */
            format: function(value) {
                return (parseFloat(value) || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',');
            }
        }
    }
</script>
